<script lang="ts">
  // selten gebrauchte Oberflächen werden erst bei Bedarf geladen (schneller Start)
  import { onMount } from 'svelte';
  import { ui } from '../game/store.ts';
  import { game } from '../game/game.ts';
  import { t } from '../i18n.ts';
  import MainMenu from './MainMenu.svelte';
  import Hud from './Hud.svelte';
  import Speech from './Speech.svelte';
  import QuizLock from './QuizLock.svelte';
  import EndCard from './EndCard.svelte';
  import { installTestApi } from '../game/testapi.ts';
  import { readSave, type SlotId } from '../game/save.ts';
  import DialogBox from './DialogBox.svelte';
  import Captions from './Captions.svelte';
  import Toasts from './Toasts.svelte';
  import { refreshAccount } from '../api/account.ts';
  import { startCloudSync, syncAfterLogin } from '../game/cloud.ts';

  import { feature } from '../../shared/features.ts';
  const path = feature.party ? location.pathname : '/';
  const phoneCode = path.startsWith('/join') ? (path.split('/')[2] ?? '') : null;
  const isHost = path.startsWith('/host');
  const isEditor = feature.editor && location.pathname.startsWith('/editor');
  const isOnline = feature.online && location.pathname.startsWith('/online');
  const onlineCode = isOnline ? (location.pathname.split('/')[2] ?? '') : '';
  import { patch } from '../game/store.ts';

  let host: HTMLDivElement;
  let menuOpen = $state(true);
  let loadError = $state('');
  let hasSave = $state(false);
  let started = $state(false);
  let dailyOpen = $state(false);
  let pubOpen = $state(false);

  let saveCheck: Promise<unknown> = Promise.resolve();
  async function play() {
    await saveCheck;
    if (!started && hasSave) await game.loadFrom('auto');
    started = true;
    menuOpen = false;
    game.resume();
  }
  async function newGame() {
    game.pause();
    await game.newGame();
    started = true;
    menuOpen = false;
    game.resume();
  }
  async function loadSlot(slot: SlotId) {
    game.pause();
    const ok = await game.loadFrom(slot);
    if (!ok) return;
    started = true;
    menuOpen = false;
    game.resume();
  }
  // Vollflächige Fenster verdecken die Szene: Darstellung anhalten (spart Akku, hält die Oberfläche flüssig).
  // Nur selbst Angehaltenes wieder starten – der Quizabend hält die Szene eigenständig an (Entscheidung 0009).
  let frozen = false;
  $effect(() => {
    const cover = pubOpen || $ui.shop || dailyOpen || $ui.cover;
    const tk = game.app?.ticker;
    if (!tk) return;
    if (cover && tk.started) {
      tk.stop();
      frozen = true;
    } else if (!cover && frozen) {
      frozen = false;
      tk.start();
    }
  });

  function openMenu() {
    menuOpen = true;
    game.pause();
  }

  onMount(async () => {
    if (feature.online && !isEditor && phoneCode === null) {
      startCloudSync();
      // angemeldet? neuerer Cloud-Stand wird übernommen (dann einmal neu laden)
      // Konto/Cloud nach dem Start der Szene prüfen
      if (!isOnline) addEventListener('dlr-ready', () => void refreshAccount().then(async (a) => a && (await syncAfterLogin()) && location.reload()), { once: true });
    }
    if (phoneCode !== null || isEditor || isOnline) return;
    try {
      performance.mark('mount');
      await game.init(host);
      performance.mark('pixi-ready');
      installTestApi();
      const qp = new URLSearchParams(location.search);
      await game.start(qp.get('chapter') ?? 'ch1', undefined, qp.get('draft') === '1');
      performance.mark('game-started');
      // Spielstand-Prüfung (IndexedDB kann beim ersten Öffnen dauern) blockiert den Start nicht
      saveCheck = readSave('auto').then((s) => (hasSave = !!s));
      performance.mark('started');
      await new Promise<void>((r) => game.app.ticker.addOnce(() => requestAnimationFrame(() => r())));
      (window as any).__dlrReadyAt = performance.now();
      (window as any).__dlrReady = true;
      dispatchEvent(new Event('dlr-ready'));
      setTimeout(() => game.sharpen(), 300);
      if (location.hash === '#play') newGame();
      if (isHost) {
        menuOpen = false;
        game.resume();
      }
    } catch (e) {
      console.error(e);
      loadError = String(e);
    }
  });
</script>

{#if isEditor}
  {#await import('../editor/EditorApp.svelte') then M}<M.default  />{/await}
{:else if isOnline}
  {#await import('../online/OnlineApp.svelte') then M}<M.default code={onlineCode} />{/await}
{:else if phoneCode !== null}
  {#await import('../party/PhoneApp.svelte') then M}<M.default code={phoneCode} />{/await}
{:else}
<div class="stage" bind:this={host} class:dim={menuOpen} class:covered={pubOpen || $ui.shop || dailyOpen || $ui.cover}></div>
{/if}

{#if isHost && $ui.screen === 'game'}
  {#await import('../party/HostApp.svelte') then M}<M.default onClose={() => (location.href = '/')} />{/await}
{/if}

{#if $ui.screen === 'game' && !menuOpen && !$ui.show && !isHost}
  <Hud onMenu={openMenu} />
  <Speech />
{/if}

{#if $ui.dialog && !menuOpen}
  <DialogBox />
{/if}

{#if $ui.quiz}
  <QuizLock session={$ui.quiz} />
{/if}

{#if $ui.ended}
  <EndCard onMenu={openMenu} />
{/if}

{#if $ui.show}
  {#await import('./show/ShowApp.svelte') then M}<M.default onClose={() => patch({ show: false })} />{/await}
{/if}

{#if menuOpen && phoneCode === null && !isHost && !isEditor && !isOnline}
  <MainMenu ready={$ui.screen === 'game'} {hasSave} {started} onPlay={play} onNew={newGame} onLoad={loadSlot} onShow={() => { menuOpen = false; game.resume(); patch({ show: true }); }} onDaily={() => (dailyOpen = true)} onPub={() => (pubOpen = true)} />
{/if}

{#if dailyOpen}
  {#await import('./DailyApp.svelte') then M}<M.default onClose={() => (dailyOpen = false)} />{/await}
{/if}

{#if feature.meta && (pubOpen || $ui.shop)}
  {#await import('./PubPanel.svelte') then M}<M.default onClose={() => ((pubOpen = false), patch({ shop: false }))} />{/await}
{/if}

{#if feature.pubLife && $ui.guest && !menuOpen}
  {#key $ui.guest}{#await import('./GuestPanel.svelte') then M}<M.default npc={$ui.guest} onClose={() => patch({ guest: null })} />{/await}{/key}
{/if}

{#if $ui.map && !menuOpen}{#await import('./MapPanel.svelte') then M}<M.default  />{/await}{/if}

{#if feature.meta}<Toasts />{/if}

{#if loadError}
  <div class="error panel" role="alert">{t('ui.loadError')}<br /><small>{loadError}</small></div>
{/if}

<Captions />

<div id="fade"></div>

<style>
  .stage {
    position: fixed;
    inset: 0;
    transition: filter 0.6s ease;
  }
  .stage.covered {
    visibility: hidden;
  }
  .stage.dim {
    filter: blur(3px) brightness(0.55);
  }
  .error {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 1.5em;
    z-index: 100;
  }
</style>
